import { Box, Flex, Text } from '@chakra-ui/react';
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { DataBoxesContainer } from 'src/components/atoms/DataBoxesContainer';
import { QuickModelNotice } from 'src/components/molecules/QuickModelNotice';
import { useFilterContext } from 'src/contexts/FilterContext';

type PageProps = {
  filterBar?: React.ReactElement | null;
  keyMetrics?: JSX.Element;
  children: React.ReactNode;
  pageTitle?: string;
  pageSubtitle?: string;
  showHeaderRightSide?: boolean;
  showGenAIInsights?: boolean;
  extraButtons?: React.ReactElement | null;
  genAiInsights?: React.ReactElement | null;
};

export const PageModelLayout: React.FC<PageProps> = ({
  filterBar,
  keyMetrics,
  pageTitle = '',
  children,
  pageSubtitle,
  extraButtons,
  genAiInsights,
}) => {
  const location = useLocation();
  const { changeCurrentModelByParametersId, modelVersionsState } =
    useFilterContext();
  const processedRef = useRef<string | null>(null);

  // Scroll to top when switching tabs
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  // Handle model selection from navigation state (e.g., when coming from Goals)
  useEffect(() => {
    const state = location.state as { modelParametersId?: string } | null;
    const modelParametersId = state?.modelParametersId;

    if (
      modelParametersId &&
      modelVersionsState.modelVersions.length > 0 &&
      processedRef.current !== modelParametersId
    ) {
      processedRef.current = modelParametersId;
      changeCurrentModelByParametersId(modelParametersId);
    }
  }, [
    location.state,
    modelVersionsState.modelVersions,
    changeCurrentModelByParametersId,
  ]);

  return (
    <Flex w="100%" direction="column">
      <Flex flex="1" direction="column" p={{ base: 4, lg: 8 }}>
        <Flex direction="column" gap={4} mb={6}>
          <Flex
            justifyContent="space-between"
            alignItems="flex-start"
            direction={{ base: 'column', lg: 'row' }}
            gap={{ base: 4, lg: 0 }}
            w="100%">
            <Flex direction="column" gap={4} flex={{ base: 1, lg: 'none' }}>
              <Text
                fontSize={32}
                fontFamily="marketingPage"
                fontWeight="bold"
                color="content.title">
                {pageTitle}
              </Text>
              {pageSubtitle && (
                <Text
                  fontSize={16}
                  color="content.muted"
                  fontFamily="marketingPage">
                  {pageSubtitle}
                </Text>
              )}
            </Flex>
            {extraButtons}
          </Flex>
          <QuickModelNotice />
        </Flex>
        {filterBar && <Box mb={6}>{filterBar}</Box>}
        {genAiInsights}
        {keyMetrics}
        <DataBoxesContainer>{children}</DataBoxesContainer>
      </Flex>
    </Flex>
  );
};
